/**
 * Mechanic shop / auto repair demo — full tour with form-filling invoice walkthrough.
 *
 * Walks through the NEW magazine-style dashboard: Recent Activity with
 * typeahead search → Top Categories → Inventory running low → Overdue
 * invoices → "View Financials" CTA → real form-filled invoice creation
 * → customer book → inventory → Financials (with Privacy PIN) → Payments
 * → Tax Reports → Settings (logo + line-item categories + tax setup).
 *
 * No "send by text" — only email + print (SMS isn't wired up).
 */
export default {
  category: "mechanic",
  title: "Run your repair shop",
  initialRoute: "/riverside-auto",

  steps: [
    // -- Greeting + dashboard tour ------------------------------------
    {
      type: "narrate",
      text: "Hi there! Welcome to My Business Portal. Let me walk you through how it runs a mechanic or repair shop, end to end.",
      duration: 11000,
    },
    {
      type: "point",
      target: '[data-testid="recent-activity"]',
      text: "Your dashboard opens on Recent Activity — every invoice and payment from the last few days in one stream, so you always know exactly what's going on in the shop right now.",
      duration: 15500,
    },
    {
      type: "point",
      target: '[data-testid="recent-invoices-search"]',
      text: "Need to find a specific invoice fast? Search right here — by invoice number, customer name, or truck number. Matches come up as you type, across your entire history.",
      duration: 15500,
    },
    {
      type: "point",
      target: '[data-testid="low-stock-card"]',
      text: "Inventory running low — every part you stock that's at or below its reorder threshold, so you never get caught short on a job. The system tracks your inventory live: every part you put on an invoice deducts from stock the moment that invoice is created, so what you see here is always current. Click any part to see your vendor and reorder it in seconds.",
      duration: 22500,
    },
    {
      type: "point",
      target: '[data-testid="overdue-invoices-card"]',
      text: "Overdue invoices — the customers who still owe you money, sorted by how late they are. You can text the whole list a payment reminder in one click — no more chasing people one at a time.",
      duration: 16000,
    },
    {
      type: "point",
      target: '[data-testid="view-financials-btn"]',
      text: "And when you want the full breakdown — revenue, profit, margins, tax owed — it's one click away on the Financials page. We'll go there in a minute.",
      duration: 13500,
    },

    // -- Invoice creation: real form-fill walkthrough -----------------
    {
      type: "click",
      target: '[data-testid="create-invoice-btn"]',
      text: "Now let's actually create an invoice from start to finish. I'll click New Invoice.",
      duration: 7500,
    },
    {
      type: "point",
      target: '[data-testid="customer-search-input"]',
      text: "First, I pick a customer. I'll start typing.",
      duration: 5500,
    },
    {
      type: "type",
      target: '[data-testid="customer-search-input"]',
      value: "Cascade",
      text: "Watch — as I type, the system searches my customer book and surfaces the match.",
      duration: 5000,
    },
    {
      type: "click",
      target: '[data-testid="customer-match-row"]',
      text: "I tap the match and the customer is locked in.",
      duration: 4500,
    },
    {
      type: "point",
      target: '[data-testid="vehicle-info-input"]',
      text: "Next, the vehicle and work order. This locks the invoice to the specific vehicle — car or truck — so you can always look back and see exactly what job it was.",
      duration: 12500,
    },
    {
      type: "type",
      target: '[data-testid="vehicle-info-input"]',
      value: "2019 Peterbilt 579 · WO #4421",
      text: "I'll type the vehicle info — year, make, model, and the work order number.",
      duration: 8000,
    },
    {
      type: "point",
      target: '[data-testid="new-item-description"]',
      text: "Now the line items. I'll add the brake job we just finished.",
      duration: 7000,
    },
    {
      type: "type",
      target: '[data-testid="new-item-description"]',
      value: "Brake pads, front + rear",
      text: "I type the description for the parts I used.",
      duration: 5500,
    },
    {
      type: "type",
      target: '[data-testid="new-item-rate"]',
      value: "120",
      text: "Then the price — one twenty for the pads.",
      duration: 5000,
    },
    {
      type: "click",
      target: '[data-testid="add-item-btn"]',
      text: "And Add — the brake pads land on the invoice as a Part.",
      duration: 5500,
    },
    {
      type: "type",
      target: '[data-testid="new-item-description"]',
      value: "Brake replacement labor",
      text: "Now the labor line for the same job.",
      duration: 5500,
    },
    {
      type: "selectOption",
      target: '[data-testid="new-item-type"]',
      optionText: "Labor",
      text: "I switch the type to Labor — that's what drives the labor-profit number on the dashboard.",
      duration: 7000,
    },
    {
      type: "type",
      target: '[data-testid="new-item-qty"]',
      value: "2",
      text: "Two hours of work.",
      duration: 4000,
    },
    {
      type: "type",
      target: '[data-testid="new-item-rate"]',
      value: "90",
      text: "At ninety an hour. The system auto-totals as I go.",
      duration: 5500,
    },
    {
      type: "click",
      target: '[data-testid="add-item-btn"]',
      text: "Add the labor line. Parts and labor are tracked separately on purpose.",
      duration: 6500,
    },
    {
      type: "point",
      target: '[data-testid="tax-exempt-summary-toggle"]',
      text: "If this customer is tax exempt — a resale account or a non-profit — I'd flip this toggle and the line is tracked under tax-exempt sales instead of taxable. We'll leave it off for this one.",
      duration: 15000,
    },
    {
      type: "point",
      target: '[data-testid="tax-rate-select"]',
      text: "Your default tax rate applies automatically. You set this once in Settings and every new invoice uses it.",
      duration: 11000,
    },
    {
      type: "point",
      target: '[data-testid="create-invoice-submit"]',
      text: "And I hit Create Invoice — done in under sixty seconds.",
      duration: 7500,
    },
    {
      type: "navigate",
      url: "/riverside-auto/invoices/demo-brake-job-001",
      text: "And here is the finished invoice on your side — the shop owner view.",
      duration: 7000,
    },
    {
      type: "narrate",
      text: "Your shop name, address, phone, and logo at the top. The customer, the vehicle, the invoice number, the date and the due date. Every line item with its description, quantity, rate, and total — parts and labor still separated so your dashboard math stays clean.",
      duration: 20000,
    },
    {
      type: "point",
      target: '[data-testid="print-invoice-btn"]',
      text: "And when I hit Print —",
      duration: 4500,
    },
    {
      type: "navigate",
      url: "/riverside-auto/invoices/demo-brake-job-001/print",
      text: "— this is exactly what your customer sees. A clean, printable invoice ready to hand them or email as a PDF.",
      duration: 13000,
    },
    {
      type: "narrate",
      text: "Subtotal, tax, and grand total at the bottom. Email it, print it, edit it, or record a payment the moment the customer settles up.",
      duration: 13000,
    },
    {
      type: "navigate",
      url: "/riverside-auto/invoices",
      text: "And back on the invoice list you can see every invoice you've ever sent, with its status — paid, pending, or overdue — at a glance.",
      duration: 12500,
    },

    // -- Customers section -------------------------------------------
    {
      type: "navigate",
      url: "/riverside-auto/customers",
      text: "The customer book. Every account with their open balance, their orders, and their full job history.",
      duration: 11000,
    },
    {
      type: "narrate",
      text: "Click any customer and you'll see every invoice they've ever paid, every vehicle you've serviced, every dollar still owed. To add customers in bulk, drop in a CSV, an Excel sheet, a Word doc, or a PDF — the AI reads the columns and imports them for you.",
      duration: 18500,
    },

    // -- Inventory ----------------------------------------------------
    {
      type: "navigate",
      url: "/riverside-auto/inventory",
      text: "Inventory: every part you stock with its vendor, your buy price, your sell price, the markup, and current stock.",
      duration: 12500,
    },
    {
      type: "narrate",
      text: "Adding parts in bulk works the same way as customers — upload a parts list in any format and the system creates the records for you. No more typing in part numbers one by one.",
      duration: 13500,
    },

    // -- Financials (NEW — Privacy PIN + isolated profit page) -------
    {
      type: "navigate",
      url: "/riverside-auto/financials",
      text: "Financials. A dedicated page just for the numbers — revenue trend, parts profit, labor profit, net profit margin — broken out across any date range you pick.",
      duration: 14500,
    },
    {
      type: "narrate",
      text: "And because this is the most sensitive screen in the whole app, you can lock it behind a privacy PIN with the toggle in the top right. Set a four-digit code and anyone who opens Financials has to punch it in — your employees can still run invoices and inventory, but only you see the profit numbers.",
      duration: 19000,
    },

    // -- Payments (NEW — its own sidebar route) ----------------------
    {
      type: "navigate",
      url: "/riverside-auto/payments",
      text: "Payments has its own page now. Connect Stripe, Square, or PayPal with one click and customers can pay any invoice online — card, Apple Pay, Google Pay — straight from the email or text we send them.",
      duration: 18500,
    },
    {
      type: "narrate",
      text: "Zelle's right there too for the customers who'd rather skip card fees — your Zelle email and phone show up on every invoice automatically. When a card payment lands, the invoice flips to Paid on its own — no manual reconciliation.",
      duration: 17000,
    },

    // -- Tax reports --------------------------------------------------
    {
      type: "navigate",
      url: "/riverside-auto/reports/tax",
      text: "Tax reports — now living inside Financials. Every invoice broken down: taxable total, tax-exempt total, the tax you collected, and the breakdown across months. Export to CSV or PDF in one click — ready for your accountant or the state portal.",
      duration: 18500,
    },

    // -- Settings -----------------------------------------------------
    {
      type: "navigate",
      url: "/riverside-auto/settings",
      text: "Settings. Your business info, branding, notifications, and tax setup — all stacked as cards on one scrollable page. No more hunting through tabs.",
      duration: 14500,
    },
    {
      type: "scroll",
      delta: 350,
      text: "Below that is your business logo.",
      duration: 4000,
    },
    {
      type: "narrate",
      text: "When you sign up, we generate a clean, category-matched logo for you with AI in about fifteen seconds. Keep it, regenerate a different variation, or upload your own — and every previous logo is saved in history so you can always switch back.",
      duration: 16500,
    },
    {
      type: "scroll",
      delta: 400,
      text: "Line-item categories. Out of the box you've got Part and Labor — but you can add your own. Tire shop? Add Tire. Detailing? Add Detail. Each category can either count toward inventory or not — your call.",
      duration: 16000,
    },
    {
      type: "scroll",
      delta: 400,
      text: "And tax setup.",
      duration: 4000,
    },
    {
      type: "narrate",
      text: "Set your default sales tax rate once and every new invoice uses it automatically — no more punching it in by hand.",
      duration: 10000,
    },

    // -- Close --------------------------------------------------------
    {
      type: "narrate",
      text: "That's the full tour. Free to start, no credit card required — click Get Started up top and your shop is on the system in minutes.",
      duration: 11500,
    },
  ],
};
